import { useMemo, useState } from 'react';
import { useTranslation } from '@eon/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PageHeader,
  Pagination,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';
import { formatRelative } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { DeviceStatusBadge, ConnectorStatusBadge } from '@/components/status/StatusBadges';
import { Pencil, Plus, Search } from 'lucide-react';
import { DeviceChargingControls } from '@/components/portal/ChargingControls';
import { EditOmDeviceDialog } from '@/components/portal/EditOmDeviceDialog';
import { RegisterDeviceDialog } from '@/components/portal/RegisterDeviceDialog';

const PAGE_SIZE_OPTIONS = [12, 24, 48];

export function DevicesPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editDevice, setEditDevice] = useState<Device | null>(null);

  const list = hooks.useMyDevices({ page, pageSize, q: q.trim() || undefined });
  const stations = hooks.useMyStations({ page: 1, pageSize: 200 });

  const stationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of stations.data?.data ?? []) {
      map.set(s.id, s.name);
    }
    return map;
  }, [stations.data?.data]);

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.devices')}
        description={list.data ? `${totalCount.toLocaleString()} ${t('nav.devices')}` : undefined}
        actions={
          <Button onClick={() => setRegisterOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('portal.devices.register')}
          </Button>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          className="pl-9"
          placeholder={t('actions.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {list.isLoading ? (
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      ) : (list.data?.data?.length ?? 0) === 0 ? (
        <p className="text-muted-foreground text-sm">{t('device.notFound')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.data?.data.map((device) => (
            <Card key={device.id} className="overflow-hidden">
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="min-w-0">
                  <CardTitle className="truncate text-base">
                    {device.displayName?.trim() || device.serialNumber}
                  </CardTitle>
                  <p className="text-muted-foreground font-mono text-xs">{device.serialNumber}</p>
                </div>
                <DeviceStatusBadge status={device.status} />
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-muted-foreground text-xs">{t('device.model')}</p>
                    <p>{device.vendor ?? '—'}</p>
                    {device.model ? (
                      <p className="text-muted-foreground text-xs">{device.model}</p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">{t('device.station')}</p>
                    <p>
                      {device.stationId
                        ? (stationNames.get(device.stationId) ?? device.stationId.slice(0, 8))
                        : t('portal.devices.noStation')}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1 text-xs">{t('device.connectors')}</p>
                  <div className="flex flex-wrap gap-1">
                    {(device.connectors ?? []).length === 0 ? (
                      <span className="text-muted-foreground text-xs">—</span>
                    ) : (
                      device.connectors.map((c) => (
                        <ConnectorStatusBadge key={c.id} id={c.id} status={c.status} />
                      ))
                    )}
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('device.lastSeen')}: {formatRelative(device.lastHeartbeatAt ?? device.lastHeartbeat)}
                </p>
                <DeviceChargingControls device={device} />
                <Button variant="outline" size="sm" className="w-full" onClick={() => setEditDevice(device)}>
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  {t('actions.edit')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}

      <RegisterDeviceDialog open={registerOpen} onOpenChange={setRegisterOpen} />
      <EditOmDeviceDialog
        device={editDevice}
        open={Boolean(editDevice)}
        onOpenChange={(o) => !o && setEditDevice(null)}
      />
    </>
  );
}
